/// <amd-module name="TFS/NetPromoterSurvey/Components/StepByStepSurveyDialog" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";

import { Label } from "OfficeFabric/Label";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";

import { IResponse } from "TFS/NetPromoterSurvey/Common";
import { ScoreChoiceGroup } from "TFS/NetPromoterSurvey/Components/ScoreChoiceGroup";
import { SubmissionDetail } from "TFS/NetPromoterSurvey/Components/SubmissionDetail";
import * as NetPromoterSurveyResources from "TFS/NetPromoterSurvey/Resources";

export interface StepByStepDialogProps {
    productName: string;
    response: IResponse;
    isAuthenticated: boolean;
    change(response: Partial<IResponse>): void;
    submit(response: IResponse, exitMethod: string): void;
    postpone(response: IResponse, exitMethod: string): void;
    close(): void;
}

interface StepByStepSurveyDialogState {
    step: number;
}

/**
 * Component implementing the step by step survey dialog
 */
export class StepByStepSurveyDialog extends VssComponent<StepByStepDialogProps, StepByStepSurveyDialogState> {
    constructor(props: any) {
        super(props);

        this.state = {
            step: 0
        };

        // Add mousedown dismiss handler for the "Thank you" page. _handleClick should close and dismiss at step === 2
        document.addEventListener("mousedown", this._handleClick);
    }

    public render(): JSX.Element {
        switch (this.state.step) {
            case 0:
                return this.step0();
            case 1:
                return this.step1();
            case 2:
                return this.step2();
            default:
                return <div />;
        }
    }

    private next = (): void => {
        if (this.state.step + 1 === 2 && this.props.response.score === undefined) {
            document.removeEventListener("mousedown", this._handleClick);
            this.props.close();
            return;
        }
        this.setState({ step: this.state.step + 1 });
    };

    /* First step. Contains score option only */
    private step0 = (): JSX.Element => {
        return (
            <ScoreChoiceGroup
                selected={this.props.response.score}
                scoreCount={10}
                label={format(NetPromoterSurveyResources.ScoreLabel, this.props.productName)}
                labelMinimum={NetPromoterSurveyResources.Score0Label}
                labelMaximum={NetPromoterSurveyResources.Score10Label}
                onChange={(response: Partial<IResponse>) => {
                    this.props.change(response);
                    this.props.submit({ ...this.props.response, ...response }, "score-click");
                }}
                onClick={() => {
                    this.next();
                }}
            />
        );
    };

    /* Second step. Contains reason field, role dropdown and submit button */
    private step1 = (): JSX.Element => {
        return (
            <div>
                <Link
                    onClick={() => {
                        this.setState({ step: 0 });
                    }}
                    target="_blank"
                >
                    <Icon iconName="Back" /> Back
                </Link>
                <SubmissionDetail
                    reason={this.props.response.reason}
                    reasonLabel={format(NetPromoterSurveyResources.Step1Title, this.props.productName, this.props.response.score)}
                    role={this.props.response.role}
                    roleLabel={NetPromoterSurveyResources.RoleLabel}
                    roleList={NetPromoterSurveyResources.RoleList}
                    rolePlaceholder={NetPromoterSurveyResources.RolePlaceholder}
                    hideContactConsented={!this.props.isAuthenticated}
                    contactConsentedLabel={NetPromoterSurveyResources.ContactMeLabel}
                    contactConsented={this.props.response.contactConsented}
                    privacyText={NetPromoterSurveyResources.PrivacyLabel}
                    privacyUrl={NetPromoterSurveyResources.PrivacyUrl}
                    onChange={this.props.change}
                    onSubmit={() => {
                        this.next();
                        this.props.submit(this.props.response, "submit");
                    }}
                />
            </div>
        );
    };

    /* Third and last step. Contains a 'Thank you' */
    private step2 = (): JSX.Element => {
        return (
            <div>
                <Label className="fontSizeM fontWeightSemiBold">{NetPromoterSurveyResources.Step2Title}</Label>
                <p>{NetPromoterSurveyResources.ThankYouBody}</p>
            </div>
        );
    };

    private _handleClick = (): void => {
        // TODO: Handle for the case where the prompt might be unmounted when this is called.
        if (this.state.step === 2) {
            document.removeEventListener("mousedown", this._handleClick);
            this.props.close();
        }
    };
}
